import { ShippingFlowSelectorOptionsEnum } from '../../../constants/addressBook';
import { INITIAL_SHIPPING_ADDRESS_FORM_STATE } from '../../../constants/shipping';
import { ICommonAddress, IShippingDetails } from '../../../types/shipping';
import { isObjectEmpty } from '../../../utils/helpers';
import { resolveOfficeDropshipEnabled } from '../utils';
import { ActionTypesEnum, TAction } from './actions';

export interface IState {
  isDelayedShipping?: boolean;
  isAddressFixed?: boolean;
  addresses?: string[];
  receiverAddress?: ICommonAddress;
  selectorValue: ShippingFlowSelectorOptionsEnum;
  isOfficeDropshipEnabled?: boolean;
}

export const getInitialState = (value?: IShippingDetails): IState => {
  const { status, is_receiver_address_fixed, org_address_ids, receiver_address, is_office_dropship_enabled } =
    value || {};

  const selectorValue = (() => {
    const isRecipientDecides = status && !is_receiver_address_fixed;
    const isRecipientDecidesFromList = status && is_receiver_address_fixed && !!(org_address_ids?.length ?? 0 > 1);
    const isFixed = !status || (status && is_receiver_address_fixed && org_address_ids?.length === 1);

    switch (true) {
      case isRecipientDecides:
        return ShippingFlowSelectorOptionsEnum.RecipientDecides;
      case isFixed:
        return ShippingFlowSelectorOptionsEnum.Fixed;
      case isRecipientDecidesFromList:
        return ShippingFlowSelectorOptionsEnum.RecipientDecidesFromList;
      default:
        return ShippingFlowSelectorOptionsEnum.Fixed;
    }
  })();

  return {
    isDelayedShipping: !!status,
    isAddressFixed: is_receiver_address_fixed,
    addresses: org_address_ids,
    receiverAddress: receiver_address,
    selectorValue,
    // A stored flag must not survive a seed state where the toggle is hidden (EX-6169)
    isOfficeDropshipEnabled: !!resolveOfficeDropshipEnabled({
      selectValue: selectorValue,
      org_address_ids,
      receiver_address,
      is_office_dropship_enabled,
    }),
  };
};

// Keeps the dropship flag in sync with the toggle's visibility: an address change that hides the
// toggle must also clear the flag, otherwise a hidden `true` still reaches the backend (EX-6169).
const withResolvedOfficeDropship = (state: IState): IState => ({
  ...state,
  isOfficeDropshipEnabled: resolveOfficeDropshipEnabled({
    selectValue: state.selectorValue,
    org_address_ids: state.addresses,
    receiver_address: state.receiverAddress,
    is_office_dropship_enabled: state.isOfficeDropshipEnabled,
  }),
});

export const reducer = (state: IState, action: TAction): IState => {
  switch (action.type) {
    case ActionTypesEnum.changeSelect:
      const isNewFixed = action.payload === ShippingFlowSelectorOptionsEnum.Fixed;
      const isNewRecipientDecides = action.payload === ShippingFlowSelectorOptionsEnum.RecipientDecides;
      const isNewRecipientDecidesFromList = action.payload === ShippingFlowSelectorOptionsEnum.RecipientDecidesFromList;
      const hasPrevReceiverAddress = !isObjectEmpty(state.receiverAddress);

      return withResolvedOfficeDropship({
        ...state,
        selectorValue: action.payload,
        ...(isNewRecipientDecides
          ? {
              isAddressFixed: false,
              receiverAddress: hasPrevReceiverAddress ? state.receiverAddress : undefined,
            }
          : {
              isAddressFixed: true,
              addresses: (() => {
                if (isNewFixed) {
                  return undefined;
                }
                // Unreachable area
                console.warn("Unreachable area in 'changeSelect' reducer");
                return state.addresses;
              })(),
              receiverAddress: (() => {
                if (isNewFixed) {
                  return hasPrevReceiverAddress ? state.receiverAddress : undefined;
                }

                if (isNewRecipientDecidesFromList) {
                  return INITIAL_SHIPPING_ADDRESS_FORM_STATE;
                }

                // Unreachable area
                console.warn("Unreachable area in 'changeSelect' reducer");
                return state.receiverAddress;
              })(),
            }),
      });

    case ActionTypesEnum.changeFixedAddress:
      return withResolvedOfficeDropship({
        ...state,
        receiverAddress: action.payload,
      });
    case ActionTypesEnum.changeAB:
      return withResolvedOfficeDropship({
        ...state,
        addresses: action.payload,
      });
    case ActionTypesEnum.changeOfficeDropship:
      return {
        ...state,
        isOfficeDropshipEnabled: action.payload,
      };
    case ActionTypesEnum.reset:
      return { ...action.payload };
    default:
      return state;
  }
};

export * from './actions';
